import { useState } from 'react';
import { verticalOffersConfidentialNotes, type InputExtractionResult } from '@ni/shared';
import { useMayWrite, useOrg, useSeesConfidential } from '../../api/app-context';
import { axisOfWrite } from '../../api/client';

import { useCreateInput, useExtractRetry } from '../../api/hooks';
import { ACCESS_CLOSE, NOTE_CONFIDENTIAL_HINT, NOTE_SAVED_CONFIDENTIAL } from '../../lib/copy';
import { plural } from '../../lib/format';
import { noteOverBy } from '../../lib/prose-limit';
import { Checkbox } from '../ui/checkbox';
import { NoteLength, noteDescribedBy } from '../ui/char-count';
import { ErrorLine } from '../ui/error-line';
import { Modal } from '../ui/modal';
import { WorkingButton } from '../ui/working-button';
import { Button } from '../ui/button';

/**
 * What landed, said once under the header the note was added from.
 *
 * **A note that is still being read has no counts to give**, and zeroes would be
 * a lie told in the shape of a result: "Added" with nothing after it reads as a
 * note that yielded nothing, which is the one thing that has not been decided
 * yet. The add now answers the moment the note is saved and the reading happens
 * behind it, so the honest sentence is that it is happening. The record's own
 * banner carries it from there, and the facts appear under it when they land.
 */
export function summaryLine(result: InputExtractionResult): string {
  /* A confidential note: filed, and by the office's own instruction never
     read. There is no reading to wait on and nothing will land under it.
     `none` is the key rather than a flag of its own because this box is the
     only door onto this sentence and a confidential note is the only `none`
     the add box can answer with: an interaction's `none` comes through the
     timeline's own endpoint, and the retry button is offered on `failed` rows
     alone. A paste still says how many rows it became. */
  if (result.input.extraction_status === 'none') {
    return result.pieces > 1
      ? `Saved as confidential in ${plural(result.pieces, 'piece', 'pieces')}. It will not be used in research.`
      : NOTE_SAVED_CONFIDENTIAL;
  }
  if (result.input.extraction_status === 'pending') {
    /* **A paste that was filed as pieces says so** (split-giant-note note,
       2026-09-14). The record is about to show one entry with eight readings
       under it, and "Saved. Reading the note now." would have the office
       wondering where their one note went. It is not an apology and not a
       warning: it is the shape their words are now in. */
    return result.pieces > 1
      ? `Saved in ${plural(result.pieces, 'piece', 'pieces')}. Reading them now.`
      : 'Saved. Reading the note now.';
  }
  const parts: string[] = [];
  if (result.created.interactions.length > 0) {
    parts.push(plural(result.created.interactions.length, 'interaction', 'interactions'));
  }
  if (result.created.claim_count > 0) {
    parts.push(plural(result.created.claim_count, 'finding', 'findings'));
  }
  if (result.created.anchor_count > 0) {
    parts.push(plural(result.created.anchor_count, 'identifying detail', 'identifying details'));
  }
  return parts.length > 0 ? `Added · ${parts.join(' · ')}` : 'Added.';
}

/**
 * The record's one add box (ui-design §4.8): one prose textarea, nothing to
 * choose. Lew dumps mixed material and the extractor sorts it — interaction
 * segments become timeline rows, everything else is note or reference material
 * read into the profile.
 *
 * The date field is plainly offered and never required: a note typed today about
 * a 2019 conversation must not win on recency. The extractor still reads dates
 * out of the prose itself.
 */
export function ProseModal({
  subjectId,
  onClose,
  onAdded,
}: {
  subjectId: string;
  onClose: () => void;
  onAdded: (result: InputExtractionResult) => void;
}) {
  const create = useCreateInput(subjectId);
  const retry = useExtractRetry(subjectId);
  const mayRetry = useMayWrite(axisOfWrite('extractRetry'));
  /* Two questions and both must say yes: whether this **organization** offers
     the mark at all, and whether this **reader** is shown notes that carry it.
     A member who would not be shown one back cannot file one, and the server
     refuses it either way. */
  const offersConfidential =
    verticalOffersConfidentialNotes(useOrg().vertical) && useSeesConfidential();

  const [prose, setProse] = useState('');
  const [contentDate, setContentDate] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [failed, setFailed] = useState<InputExtractionResult | null>(null);

  const submit = () => {
    /* The three the button is quiet for, said again where the write is: an empty
       box, a note past the cap, and a create already in the air. */
    if (prose.trim().length === 0 || noteOverBy(prose) > 0 || create.isPending) return;
    create.mutate(
      {
        prose: prose.trim(),
        ...(contentDate.length > 0 ? { content_date: contentDate } : {}),
        /* Sent only when it is true and only where it is offered: the body is
           strict, and the server refuses the mark for a vertical without it. */
        ...(offersConfidential && confidential ? { confidential: true } : {}),
      },
      {
        onSuccess: (result) => {
          /* `pending` closes exactly as `done` does. The note is saved, which is
             what the office asked for, and the reading is a thing the record
             page now says out loud — holding the modal open over it would be
             asking a person to watch a queue. Only a reading that has already
             failed keeps them here, because that one has a button. */
          if (result.input.extraction_status === 'failed') {
            setFailed(result);
            return;
          }
          onAdded(result);
          onClose();
        },
      },
    );
  };

  if (failed) {
    return (
      <ProseFailedView
        mayRetry={mayRetry}
        working={retry.isPending}
        error={retry.isError ? retry.error : null}
        onRetry={() =>
          retry.mutate(failed.input.id, {
            onSuccess: (result) => {
              /* `pending` leaves here the way the first add would have: the note
                 is being read and the record page says so. */
              if (result.input.extraction_status === 'failed') return;
              onAdded(result);
              onClose();
            },
          })
        }
        onClose={onClose}
      />
    );
  }

  return (
    <ProseModalView
      prose={prose}
      contentDate={contentDate}
      confidential={offersConfidential ? confidential : null}
      working={create.isPending}
      error={create.isError ? create.error : null}
      onProse={setProse}
      onContentDate={setContentDate}
      onConfidential={setConfidential}
      onSubmit={submit}
      onClose={onClose}
    />
  );
}

/**
 * **The box as it is on the page**, split from the writer the way the
 * organisation lookup's is and for the same reason: this package's suite has no
 * DOM and no query client, so what the office is offered is asserted against
 * the view's markup rather than through a press.
 *
 * **Capped since 2026-09-13** (owner ruling: "add a character limit for
 * individual notes, just on the UI for now"). Add blocks on the extraction that
 * reads the note, and a 7,313-character profile pasted into a box this size ran
 * past the transport's 180-second ceiling three times before the create gave up.
 * Nothing is truncated and no `maxLength` is set — the office must be able to
 * see the whole of what they pasted — so the count says how far over, the line
 * under it says the way through is two notes, and Add goes quiet until it is.
 *
 * The note's own well is the panel's rather than a `.field`'s, so the over state
 * is carried on the control itself (`.modal-panel textarea.over`).
 *
 * **`confidential` is `null` where the org's vertical does not offer the mark**
 * (ruled 2026-09-19), and the checkbox is not on the page at all — not disabled,
 * not explained, absent — so an office that cannot use it is not asked to
 * wonder what it is. Where it is offered, the box is one checkbox and a `(?)`
 * whose hover says the whole of the rule: the note stays on the record and is
 * not referenced in research.
 */
/**
 * **The note landed and the reading did not** — split from the writer the way
 * {@link ProseModalView} is, so every state of it is reachable from a fixture
 * rather than only from a mutation's callback.
 *
 * `mayRetry` is the gate, and it is **here on the write** rather than on the
 * control that opened the box: reading a note again spends research budget, so
 * `extract-retry` is the owner's, and a member whose writing stops at notes
 * would otherwise meet a 403 on the one flow this app gives them — at the moment
 * something has already gone wrong. What is true for them is the line above:
 * the note is saved and on the record. Close is their way out.
 */
export function ProseFailedView({
  mayRetry,
  working,
  error,
  onRetry,
  onClose,
}: {
  mayRetry: boolean;
  working: boolean;
  error: unknown;
  onRetry: () => void;
  onClose: () => void;
}) {
  return (
    <Modal label="Add" onClose={onClose}>
      <div className="modal-head">Saved; the note could not be read</div>
      {/* One note was added, so one note is what this names. The banner's rows
          say the same of every other note on the record. */}
      <div className="modal-line">Saved. The note could not be read into the profile.</div>
      <div className="modal-actions">
        {/* Disabled while the post is out, and still reading `Retry`: the word
            for what is happening is the record's to say, out of the status it
            answers with, not this button's out of its own flag. */}
        {mayRetry ? (
          <WorkingButton label="Retry" workingLabel="Retry" working={working} onClick={onRetry} />
        ) : null}
        <Button variant="act" onClick={onClose}>
          {ACCESS_CLOSE}
        </Button>
      </div>
      {error ? <ErrorLine error={error} /> : null}
    </Modal>
  );
}

export function ProseModalView({
  prose,
  contentDate,
  confidential,
  working,
  error,
  onProse,
  onContentDate,
  onConfidential,
  onSubmit,
  onClose,
}: {
  prose: string;
  contentDate: string;
  confidential: boolean | null;
  working: boolean;
  error: unknown;
  onProse: (next: string) => void;
  onContentDate: (next: string) => void;
  onConfidential: (next: boolean) => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const over = noteOverBy(prose);

  return (
    <Modal label="Add a note" onClose={onClose}>
      <div className="modal-head">Add a note</div>
      <textarea
        id="add-note"
        className={over > 0 ? 'over' : undefined}
        rows={5}
        aria-label="Note"
        placeholder="What happened, what you heard, who said it"
        value={prose}
        aria-invalid={over > 0 || undefined}
        aria-describedby={noteDescribedBy('add-note', prose)}
        onChange={(event) => onProse(event.target.value)}
      />
      <NoteLength id="add-note" value={prose} />

      {/* `last` is the field the panel closes on, and which one that is
          depends on whether the confidential mark is offered below. */}
      <div className={confidential === null ? 'field last' : 'field'}>
        <label className="label" htmlFor="add-date">
          When was this?
        </label>
        <input
          id="add-date"
          type="date"
          value={contentDate}
          onChange={(event) => onContentDate(event.target.value)}
        />
        <div className="field-note">Optional.</div>
      </div>

      {confidential === null ? null : (
        <div className="field last">
          <Checkbox className="picker-option" checked={confidential} onChange={onConfidential}>
            Confidential
            {/* A click on the mark is swallowed: it sits inside the label, and
                a question mark that flipped the checkbox would answer the wrong
                question. */}
            <span
              className="hint-mark"
              data-hint={NOTE_CONFIDENTIAL_HINT}
              onClick={(event) => event.preventDefault()}
            >
              ?
            </span>
          </Checkbox>
        </div>
      )}

      {error ? <ErrorLine error={error} /> : null}

      <div className="modal-actions">
        <WorkingButton
          label="Add"
          workingLabel="Adding…"
          working={working}
          disabled={prose.trim().length === 0 || over > 0}
          onClick={onSubmit}
        />
        <Button variant="act" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
